import React from "react";
import { Grid, Label, Header, Divider, Input, Checkbox, Dropdown, Icon, Popup, Button, Loader } from "semantic-ui-react";
import isEmpty from "lodash/isEmpty";
import { DateInput, DatesRangeInput } from "semantic-ui-calendar-react";
import { Formik, Form } from "formik";

import { object, mixed } from "yup";

import AppButton from "../Common/AppButton";
import BorderlessLabel from "../Common/BorderlessLabel";
import {
  INVOICE_STATUS,
  IN_PROGRESS_STATUS,
  PAYMENT_STATUS,
  BULK_ACTIONS,
  BULK_ACTIONS_BY_STATUS,
  TO_APPROVE_STATUS,
  PAYMENT_PENDING_STATUS
} from "./constants";
import { ConfirmationModal } from "../Common/ConfirmationModal";
import { isoToDate, DATE_FORMAT_DATE_INPUT, currentDate } from "../../services/date-helper.service";
import { isFloatOrInteger } from "../Common/Validate";
import "./InvoiceList.css";

const disableButtons = (status, paymentStatus, action) =>
  status === PAYMENT_PENDING_STATUS &&
  (isEmpty(paymentStatus) || paymentStatus === "open") &&
  (action === "RETRY_PAYMENT" || action === "CLOSE" || action === "SKIP_PAYMENT");

const disabledButtonMessage = "In order to enable please select the failed value from the payment status filter";

const isActionEnabled = (
  action,
  status,
  postPaymentsActive,
  isInternalInvoice,
  paymentStatusFilter,
  isNetsuiteApiImportEnabled
) => {
  if (isInternalInvoice && action === "Delete" && status === TO_APPROVE_STATUS) {
    return true;
  }
  if (isInternalInvoice && action === "Promote" && status === IN_PROGRESS_STATUS) {
    return true;
  }

  if (
    postPaymentsActive &&
    action === "Verify payment" &&
    status === PAYMENT_PENDING_STATUS &&
    paymentStatusFilter === "open"
  ) {
    return true;
  }

  if (action === BULK_ACTIONS.NETSUITE_API_EXPORT && !isNetsuiteApiImportEnabled && status === "toExport") {
    return false;
  }

  if (postPaymentsActive) return BULK_ACTIONS_BY_STATUS[status].includes(action) && action !== BULK_ACTIONS.APPROVE;
  else
    return (
      BULK_ACTIONS_BY_STATUS[status].includes(action) &&
      action !== BULK_ACTIONS.APPROVE_PAY &&
      action !== BULK_ACTIONS.APPROVE_NO_PAY
    );
};

const bulkActionList = (
  filter,
  getItems,
  handleBulkAction,
  toggleBulkAction,
  postPaymentsActive,
  isInternalInvoice,
  isNetsuiteApiImportEnabled
) =>
  Object.entries(isInternalInvoice ? { ...BULK_ACTIONS, ...{ DELETE: "Delete", PROMOTE: "Promote" } } : BULK_ACTIONS)
    .map(([action, description], index) =>
      isActionEnabled(
        description,
        filter.status,
        postPaymentsActive,
        isInternalInvoice,
        filter.paymentStatus,
        isNetsuiteApiImportEnabled
      ) ? (
        <ConfirmationModal
          key={index}
          header={description}
          trigger={
            <Button
              color="orange"
              disabled={disableButtons(filter.status, filter.paymentStatus, action)}
              data-tooltip={disableButtons(filter.status, filter.paymentStatus, action) ? disabledButtonMessage : null}
              data-position="bottom center"
              data-test={"btn_" + action.toLowerCase()}
            >
              {description}
            </Button>
          }
          onOkClicked={() => {
            handleBulkAction(description, getItems)();
            toggleBulkAction();
          }}
          content={`${description} selected invoices ?`}
        />
      ) : null
    )
    .filter(x => x !== null)
    .reduce((acc, item, index) => [...acc, item, <Button.Or key={100 - index} />], [])
    .slice(0, -1);

const InvoiceRow = ({
  selectAllInvoicesProps,
  isAllRecordsSelected,
  invoice,
  handleApprove,
  handleClose,
  isDelivered,
  showPaymentColumns,
  isOpen,
  onOpen,
  isSelected,
  onCheck,
  isAnyBulkAction,
  postPaymentsActive,
  isInternalInvoice,
  row
}) => (
  <Grid.Row data-test={`invoices-tr-${row}`}>
    {postPaymentsActive && (
      <>
        <Grid.Column width={1} textAlign="center">
          <Checkbox data-test="customerAutopay" name="customerAutopay" checked={invoice.customer.autopay} disabled={true} />
        </Grid.Column>
        <Grid.Column width={1} textAlign="center">
          <Checkbox
            data-test="customerHasWalletId"
            name="customerHasWalletId"
            checked={!isEmpty(invoice.customer.walletId)}
            disabled={true}
          />
        </Grid.Column>
      </>
    )}
    <Grid.Column width={2} textAlign="center" data-test="customer_name">
      {invoice.customer.company}
    </Grid.Column>
    <Grid.Column width={isDelivered ? 2 : 3} textAlign="center" data-test="purchased_products">
      {invoice.productTags.productNames &&
        invoice.productTags.productNames.map((productTag, index) => (
          <div key={index}>
            <BorderlessLabel key={index}>
              {productTag} - {invoice.productTags.quantities[index]}
            </BorderlessLabel>
          </div>
        ))}
    </Grid.Column>
    <Grid.Column width={2} textAlign="center" data-test="billing_period">
      {((invoice.billingPeriodStart && invoice.billingPeriodEnd) || !isInternalInvoice) && (
        <>
          {invoice.billingPeriodStart}/<br />
          {invoice.billingPeriodEnd}
        </>
      )}
    </Grid.Column>
    <Grid.Column width={1} textAlign="center" data-test="exclusive_amount">
      {invoice.exclusiveAmount}
    </Grid.Column>
    <Grid.Column width={1} textAlign="center" data-test="inclusive_amount">
      {invoice.inclusiveAmount}
    </Grid.Column>
    <Grid.Column width={1} textAlign="center" data-test="currency">
      {invoice.customer.currency}
    </Grid.Column>
    <Grid.Column width={1} textAlign="center" data-test="vat_percentage">
      {(invoice.vatPercentage || isInternalInvoice) && invoice.vatPercentage + "%"}
    </Grid.Column>
    {showPaymentColumns ? (
      <Grid.Column width={1} textAlign="center">
        <Popup
          pinned
          open={isOpen}
          on="click"
          data-test="popup_details"
          trigger={
            <AppButton basic={!isOpen} style={{ padding: "small" }} data-test="popup_btn" onClick={() => onOpen(invoice.id)}>
              More details
            </AppButton>
          }
        >
          {isDelivered && (
            <>
              <Header data-test="up_doc_ref">UP Doc Ref</Header>
              {invoice.upDocRef}
              <br />
            </>
          )}
          {!isEmpty(invoice.approvedAt) && (
            <>
              <Header data-test="approved_date">Approved Date</Header>
              {isoToDate(invoice.approvedAt)}
              <br />
            </>
          )}
          {!isEmpty(invoice.exportedAt) && (
            <>
              <Header data-test="last_export_date">Last export date</Header>
              {isoToDate(invoice.exportedAt)}
              <br />
            </>
          )}

          {!isEmpty(invoice.paymentReference) && (
            <>
              <Header data-test="payment_reference">Payment Reference</Header>
              {invoice.paymentReference}
              <br />
            </>
          )}

          {!isEmpty(invoice.paymentMethod) && (
            <>
              <Header data-test="payment_method">Payment Method</Header>
              {invoice.paymentMethod}
              <br />
            </>
          )}

          <Header data-test="payment_status">Payment Status</Header>
          {invoice.paymentStatus}
          {["paid", "submitted"].includes(invoice.paymentStatus) && <Icon color="green" name="check circle" />}
          <br />

          {!isEmpty(invoice.paymentStatusMessage) && (
            <>
              <Header data-test="payment_status_message">Payment Status Message</Header>
              {invoice.paymentStatusMessage}
              <br />
            </>
          )}

          <Header data-test="slug">Slug</Header>
          {invoice.id}
          <br />

          <Header data-test="slug">Netsuite API Exported</Header>
          {invoice.netsuiteImported ? <Icon color="green" name="check circle" /> : <Icon color="red" name="close" />}
          <br />

          {!isEmpty(invoice.netsuiteImportedMessage) && (
            <>
              <Header data-test="slug">Netsuite Export Message</Header>
              {invoice.netsuiteImportedMessage}
              <br />
            </>
          )}
          {!isEmpty(invoice.netsuiteExternalId) && (
            <>
              <Header data-test="slug">Netsuite External ID</Header>
              {invoice.netsuiteExternalId}
              <br />
            </>
          )}
          {!isEmpty(invoice.sepaBatchId) && (
            <>
              <Header data-test="slug">SEPA Batch ID</Header>
              {invoice.sepaBatchId}
              <br />
            </>
          )}
        </Popup>
      </Grid.Column>
    ) : (
      <Grid.Column width={1} textAlign="center">
        {invoice.status === INVOICE_STATUS.APPROVED_BY_USER && <AppButton color="green">Approved</AppButton>}
        {invoice.status === INVOICE_STATUS.CLOSED_BY_USER && <AppButton color="green">Closed</AppButton>}
        {invoice.status === INVOICE_STATUS.PROCESSED_BY_USER && <AppButton color="green">Processed</AppButton>}
      </Grid.Column>
    )}
    <Grid.Column verticalAlign="top" width={1} textAlign="center" data-test="checkbox_select">
      {isAnyBulkAction && !selectAllInvoicesProps.isBackendProcessingOngoing && (
        <Checkbox
          disabled={invoice.status === INVOICE_STATUS.PROCESSED_BY_USER || selectAllInvoicesProps.isFetchingInProgress}
          checked={isSelected && invoice.status !== INVOICE_STATUS.PROCESSED_BY_USER}
          onChange={(e, data) => {
            if (!isAllRecordsSelected) {
              onCheck(invoice.id);
            } else {
              data.checked
                ? selectAllInvoicesProps.removeFromDeselectedRecords(invoice.id)
                : selectAllInvoicesProps.addToDeselectedRecords(invoice.id);
            }
          }}
        />
      )}

      {selectAllInvoicesProps.isBackendProcessingOngoing && isSelected && (
        <Grid.Column width={1} textAlign="center">
          <AppButton color="green">Processing</AppButton>
        </Grid.Column>
      )}

      {!isSelected && invoice.status === INVOICE_STATUS.PROCESS_ERROR && <Icon color="red" fitted name="exclamation" />}
    </Grid.Column>
  </Grid.Row>
);

const InvoiceList = ({
  selectAllInvoicesProps,
  invoices,
  filter,
  filterInvoices,
  filterByBillingPeriod,
  filterByExportedPeriod,
  handleApprove,
  handleClose,
  handlePromote,
  statusFilter,
  onOpen,
  openedInvoice,
  bulk,
  postPaymentsActive,
  handleBulkAction,
  autoFocus,
  toggleFilter,
  filterOptions,
  errorHandling,
  errors,
  isInternalInvoice,
  selectedBillingPeriod,
  isNetsuiteApiImportEnabled
}) => {
  const isDelivered = statusFilter === INVOICE_STATUS.DELIVERED;
  const showPaymentColumns = ["paymentPending", INVOICE_STATUS.DELIVERED, "toExport", INVOICE_STATUS.EXPORTED].includes(
    statusFilter
  );

  const paymentPendingFilterOptions = [
    { text: "FAILED", value: "failed", key: "failed" },
    { text: "OPEN", value: "open", key: "open" },
    { text: "UNPAID", value: "unpaid", key: "unpaid" }
  ];

  const bulkActions = bulkActionList(
    filter,
    bulk.getItems,
    handleBulkAction,
    bulk.toggleBulkAction,
    postPaymentsActive,
    isInternalInvoice,
    isNetsuiteApiImportEnabled
  );
  const isAnyBulkAction = bulkActions.length >= 1;

  return (
    <Grid padded>
      <Grid.Row textAlign="right" verticalAlign="middle">
        <Grid.Column textAlign="left" width={2}>
          <h3>INVOICES</h3>
        </Grid.Column>
        <Grid.Column width={1}>
          <Label size="large" circular color="black" data-test="count_visible_records">
            {invoices.length || 0}
          </Label>
        </Grid.Column>
        <Grid.Column width={13}>
          {!selectAllInvoicesProps.isFetchingInProgress && !selectAllInvoicesProps.isBackendProcessingOngoing && (
            <Button.Group
              style={{
                visibility: (bulk.isAnySelected() || bulk.isAllRecordsSelected) && isAnyBulkAction ? "inherit" : "hidden"
              }}
              labeled
            >
              {isAnyBulkAction ? bulkActions : <Button>space</Button>}
            </Button.Group>
          )}

          {selectAllInvoicesProps.isBackendProcessingOngoing && (
            <Loader active inline>
              <Label color="red">An invoices action is currently running. Please wait!</Label>
            </Loader>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row verticalAlign="middle">
        {statusFilter === IN_PROGRESS_STATUS && isInternalInvoice && (
          <Grid.Column width={3} textAlign="right">
            <DateInput
              dateFormat={DATE_FORMAT_DATE_INPUT}
              name="billingPeriodInProgress"
              data-test="billingPeriodInProgress"
              closable
              animation="none"
              clearable
              autoComplete="off"
              clearIcon={<Icon name="remove" color="red" />}
              placeholder="Billing period"
              value={isEmpty(filter.billingPeriodInProgress) ? "" : isoToDate(filter.billingPeriodInProgress)}
              iconPosition="left"
              onChange={filterByBillingPeriod}
              maxDate={currentDate()}
            />
          </Grid.Column>
        )}
        {statusFilter === IN_PROGRESS_STATUS && isInternalInvoice && (
          <Grid.Column width={2}>
            <label>
              <div>{selectedBillingPeriod}</div>
            </label>
          </Grid.Column>
        )}
        {statusFilter === IN_PROGRESS_STATUS && isInternalInvoice && (
          <Grid.Column width={4}>
            <Input
              icon="search"
              fluid
              placeholder="Search by owner or assignee"
              onChange={e => filterInvoices(e, { name: "customerName", value: e.target.value })}
              value={filter.customerName}
              data-test="searchByOwnerOrAssignee"
            />
          </Grid.Column>
        )}
        {statusFilter === "exported" && (
          <Grid.Column width={3}>
            <DatesRangeInput
              name="exportedPeriod"
              data-test="exportedPeriod"
              dateFormat={DATE_FORMAT_DATE_INPUT}
              animation="none"
              fluid
              clearable
              closable
              autoComplete="off"
              onKeyDown={e => e.preventDefault()}
              clearIcon={<Icon name="remove" color="red" />}
              minDate={isEmpty(filter.exportedPeriodFrom) ? "" : filter.exportedPeriodFrom}
              placeholder="Exported date from - to"
              value={isEmpty(filter.exportedPeriodFrom) ? "" : `${filter.exportedPeriodFrom} - ${filter.exportedPeriodTo}`}
              iconPosition="left"
              onChange={filterByExportedPeriod}
            />
          </Grid.Column>
        )}
        <Grid.Column width={3} textAlign="left">
          {![IN_PROGRESS_STATUS, TO_APPROVE_STATUS].includes(statusFilter) && (
            <Dropdown
              name="paymentStatus"
              data-test="paymentStatus"
              selection
              multiple={statusFilter !== "paymentPending"}
              clearable
              placeholder="Payment status"
              onChange={(e, data) => {
                filterInvoices(e, { name: data.name, value: data.value });
                bulk.toggleBulkAction();
              }}
              value={statusFilter !== "paymentPending" ? filter.paymentStatus || [] : filter.paymentStatus}
              options={
                statusFilter !== "paymentPending"
                  ? Object.entries(PAYMENT_STATUS).map(([k, v]) => ({ text: k, value: v, key: v }))
                  : paymentPendingFilterOptions
              }
            />
          )}
        </Grid.Column>
        {statusFilter === "toExport" && (
          <Grid.Column width={4}>
            <Dropdown
              name="exportStatus"
              data-test="exportStatus"
              selection
              clearable
              placeholder="Export status"
              onChange={(e, data) => {
                filterInvoices(e, { name: data.name, value: data.value });
                bulk.toggleBulkAction();
              }}
              value={filter.exportStatus}
              options={[{ text: "FAILED", value: "false", key: "false" }]}
            />
          </Grid.Column>
        )}
        <Grid.Column width={10} />
      </Grid.Row>
      <Grid.Row data-test="filter_result">
        <Grid.Column width={1} textAlign="center">
          {filterOptions.customerHasAutopayFilterByOpen && (
            <Checkbox
              name="customerHasAutopay"
              data-test="customerHasAutopay"
              label={
                <label>
                  <div>Customer Autopay</div>
                </label>
              }
              onChange={(e, data) => {
                filterInvoices(e, { name: data.name, value: data.checked });
              }}
              checked={filter.customerHasAutopay === true}
            />
          )}
        </Grid.Column>
        <Grid.Column width={1} textAlign="center">
          {filterOptions.customerHasWalletIdFilterByOpen && (
            <Checkbox
              name="customerHasWalletId"
              data-test="customerHasWalletId"
              label={
                <label>
                  <div>Customer WalletId</div>
                </label>
              }
              onChange={(e, data) => {
                filterInvoices(e, { name: data.name, value: data.checked });
              }}
              checked={filter.customerHasWalletId === true}
            />
          )}
        </Grid.Column>
        <Grid.Column width={2} textAlign="center">
          {filterOptions.customerNameFilterByOpen && (
            <Input
              icon="search"
              autoFocus={autoFocus.customerNameAutoFocus}
              placeholder="Search by customer..."
              value={filter.customerName || ""}
              onChange={filterInvoices}
              name="customerName"
              data-test="customerName"
            />
          )}
        </Grid.Column>
        <Grid.Column width={2} textAlign="center" />
        <Grid.Column width={5} textAlign="center">
          {filterOptions.billingPeriodFilterByOpen && (
            <Grid columns={2}>
              <Grid.Column textAlign="center">
                <DateInput
                  dateFormat={DATE_FORMAT_DATE_INPUT}
                  name="billingPeriodStartFrom"
                  data-test="billingPeriodStartFrom"
                  closable
                  animation="none"
                  clearable
                  autoComplete="off"
                  onKeyDown={e => e.preventDefault()}
                  clearIcon={<Icon name="remove" color="red" />}
                  maxDate={isEmpty(filter.billingPeriodStartTo) ? "" : isoToDate(filter.billingPeriodStartTo)}
                  placeholder="Billing period start"
                  value={isEmpty(filter.billingPeriodStartFrom) ? "" : isoToDate(filter.billingPeriodStartFrom)}
                  iconPosition="left"
                  onChange={filterByBillingPeriod}
                />
                <Divider horizontal>Start Between</Divider>
                <DateInput
                  dateFormat={DATE_FORMAT_DATE_INPUT}
                  name="billingPeriodStartTo"
                  data-test="billingPeriodStartTo"
                  closable
                  animation="none"
                  clearable
                  autoComplete="off"
                  onKeyDown={e => e.preventDefault()}
                  clearIcon={<Icon name="remove" color="red" />}
                  initialDate={isEmpty(filter.billingPeriodStartFrom) ? "" : isoToDate(filter.billingPeriodStartFrom)}
                  minDate={isEmpty(filter.billingPeriodStartFrom) ? "" : isoToDate(filter.billingPeriodStartFrom)}
                  placeholder="Billing period start"
                  value={isEmpty(filter.billingPeriodStartTo) ? "" : isoToDate(filter.billingPeriodStartTo)}
                  iconPosition="left"
                  onChange={filterByBillingPeriod}
                />
              </Grid.Column>
              <Grid.Column textAlign="center">
                <DateInput
                  dateFormat={DATE_FORMAT_DATE_INPUT}
                  name="billingPeriodEndFrom"
                  data-test="billingPeriodEndFrom"
                  closable
                  animation="none"
                  clearable
                  autoComplete="off"
                  onKeyDown={e => e.preventDefault()}
                  clearIcon={<Icon name="remove" color="red" />}
                  maxDate={isEmpty(filter.billingPeriodEndTo) ? "" : isoToDate(filter.billingPeriodEndTo)}
                  placeholder="Billing period end"
                  value={isEmpty(filter.billingPeriodEndFrom) ? "" : isoToDate(filter.billingPeriodEndFrom)}
                  iconPosition="left"
                  onChange={filterByBillingPeriod}
                />
                <Divider horizontal>End Between</Divider>
                <DateInput
                  dateFormat={DATE_FORMAT_DATE_INPUT}
                  name="billingPeriodEndTo"
                  data-test="billingPeriodEndTo"
                  closable
                  animation="none"
                  clearable
                  autoComplete="off"
                  onKeyDown={e => e.preventDefault()}
                  clearIcon={<Icon name="remove" color="red" />}
                  initialDate={isEmpty(filter.billingPeriodEndFrom) ? "" : isoToDate(filter.billingPeriodEndFrom)}
                  minDate={isEmpty(filter.billingPeriodEndFrom) ? "" : isoToDate(filter.billingPeriodEndFrom)}
                  placeholder="Billing period end"
                  value={isEmpty(filter.billingPeriodEndTo) ? "" : isoToDate(filter.billingPeriodEndTo)}
                  iconPosition="left"
                  onChange={filterByBillingPeriod}
                />
              </Grid.Column>
            </Grid>
          )}
        </Grid.Column>
        <Grid.Column width={2} textAlign="center">
          {filterOptions.invoiceAmountFilterByOpen && (
            <Formik
              initialValues={{ invoiceAmountFrom: filter.invoiceAmountFrom, invoiceAmountTo: filter.invoiceAmountTo }}
              validateOnChange
              validationSchema={object().shape({
                invoiceAmountFrom: mixed().test("float/integer format", "Invalid number format", value =>
                  isFloatOrInteger(value)
                ),
                invoiceAmountTo: mixed().test("float/integer format", "Invalid number format", value =>
                  isFloatOrInteger(value)
                )
              })}
              render={({ values, isValid, handleChange, setFieldValue, setFieldTouched, errors }) => {
                return (
                  <Form>
                    <Grid centered columns={1}>
                      <Grid.Column>
                        {!isEmpty(errors.invoiceAmountFrom) && (
                          <Label basic color="red" pointing="below">
                            {errors.invoiceAmountFrom}
                          </Label>
                        )}
                        <Input
                          name="invoiceAmountFrom"
                          data-test="invoiceAmountFrom"
                          fluid
                          autoFocus={autoFocus.invoiceAmountFromAutoFocus}
                          icon="search"
                          placeholder="Amount from"
                          value={values.invoiceAmountFrom || ""}
                          onChange={(e, data) => {
                            setFieldValue("invoiceAmountFrom", data.value);
                            if (isFloatOrInteger(data.value)) filterInvoices(e, data);
                          }}
                        />
                        <Divider horizontal>Amount Between</Divider>
                        {!isEmpty(errors.invoiceAmountTo) && (
                          <Label basic color="red" pointing="below">
                            {errors.invoiceAmountTo}
                          </Label>
                        )}
                        <Input
                          name="invoiceAmountTo"
                          data-test="invoiceAmountTo"
                          fluid
                          autoFocus={autoFocus.invoiceAmountToAutoFocus}
                          icon="search"
                          placeholder="Amount to"
                          value={values.invoiceAmountTo || ""}
                          onChange={(e, data) => {
                            setFieldValue("invoiceAmountTo", data.value);
                            if (isFloatOrInteger(data.value)) filterInvoices(e, data);
                          }}
                        />
                      </Grid.Column>
                    </Grid>
                  </Form>
                );
              }}
            />
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row data-test="table_header">
        {postPaymentsActive && (
          <>
            <Grid.Column width={1} as={Header} size="small" textAlign="center">
              <AppButton
                name="customerHasAutopay"
                data-test="customerHasAutopay"
                style={{ padding: "small" }}
                onClick={(e, data) => {
                  toggleFilter(data.name, filterOptions.customerHasAutopayFilterByOpen);
                  filterInvoices(e, {
                    name: data.name,
                    value: filterOptions.customerHasAutopayFilterByOpen ? "" : !filterOptions.customerHasAutopayFilterByOpen
                  });
                }}
                size="large"
                basic={!filterOptions.customerHasAutopayFilterByOpen}
              >
                <Icon name="filter" />
                AP
              </AppButton>
            </Grid.Column>
            <Grid.Column width={1} as={Header} size="small" textAlign="center">
              <AppButton
                name="customerHasWalletId"
                data-test="customerHasWalletId"
                style={{ padding: "small" }}
                onClick={(e, data) => {
                  toggleFilter(data.name, filterOptions.customerHasWalletIdFilterByOpen);
                  filterInvoices(e, {
                    name: data.name,
                    value: filterOptions.customerHasWalletIdFilterByOpen
                      ? ""
                      : !filterOptions.customerHasWalletIdFilterByOpen
                  });
                }}
                size="large"
                basic={!filterOptions.customerHasWalletIdFilterByOpen}
              >
                <Icon name="filter" />
                Wallet Id
              </AppButton>
            </Grid.Column>
          </>
        )}
        <Grid.Column width={2} as={Header} size="small" textAlign="center">
          {(statusFilter !== IN_PROGRESS_STATUS || !isInternalInvoice) && (
            <AppButton
              name="customerName"
              data-test="customerName"
              style={{ padding: "small" }}
              onClick={(e, data) => {
                toggleFilter(data.name, filterOptions.customerNameFilterByOpen);
              }}
              size="large"
              basic={!filterOptions.customerNameFilterByOpen}
            >
              <Icon name="filter" />
              Customer Name
            </AppButton>
          )}
          {statusFilter === IN_PROGRESS_STATUS && isInternalInvoice && "Customer Name"}
        </Grid.Column>
        <Grid.Column width={isDelivered ? 2 : 3} as={Header} size="small" textAlign="center">
          Purchased Products - Qty.
        </Grid.Column>
        <Grid.Column width={2} as={Header} size="small" textAlign="center">
          {(statusFilter !== IN_PROGRESS_STATUS || !isInternalInvoice) && (
            <>
              <AppButton
                name="billingPeriod"
                data-test="billingPeriod"
                style={{ padding: "small" }}
                onClick={(e, data) => {
                  toggleFilter(data.name, filterOptions.billingPeriodFilterByOpen);
                }}
                size="large"
                basic={!filterOptions.billingPeriodFilterByOpen}
              >
                <Icon name="filter" />
                Billing Period
              </AppButton>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={1} as={Header} size="small" textAlign="center">
          {(statusFilter !== IN_PROGRESS_STATUS || !isInternalInvoice) && (
            <AppButton
              name="invoiceAmount"
              data-test="invoiceAmount"
              style={{ padding: "small" }}
              onClick={(e, data) => {
                toggleFilter(data.name, filterOptions.invoiceAmountFilterByOpen);
              }}
              basic={!filterOptions.invoiceAmountFilterByOpen}
              size="large"
            >
              <Icon name="filter" />
              Total Amount
              <br /> (VAT excl)
            </AppButton>
          )}
          {statusFilter === IN_PROGRESS_STATUS && isInternalInvoice && (
            <>
              Total Amount <br />
              (VAT excl)
            </>
          )}
        </Grid.Column>
        <Grid.Column width={1} as={Header} size="small" textAlign="center">
          {(statusFilter !== IN_PROGRESS_STATUS || !isInternalInvoice) && (
            <AppButton
              name="invoiceAmount"
              data-test="invoiceAmount"
              style={{ padding: "small" }}
              onClick={(e, data) => {
                toggleFilter(data.name, filterOptions.invoiceAmountFilterByOpen);
              }}
              basic={!filterOptions.invoiceAmountFilterByOpen}
              size="large"
            >
              <Icon name="filter" />
              Total Amount
              <br /> (VAT incl)
            </AppButton>
          )}
          {statusFilter === IN_PROGRESS_STATUS && isInternalInvoice && (
            <>
              Total Amount <br />
              (VAT incl)
            </>
          )}
        </Grid.Column>
        <Grid.Column width={1} as={Header} size="small" textAlign="center">
          Currency
        </Grid.Column>
        <Grid.Column width={1} as={Header} size="small" textAlign="center">
          Vat %
        </Grid.Column>

        <Grid.Column width={2} as={Header} size="small" textAlign="right">
          <Grid>
            <Grid.Row columns={1}>
              <Grid.Column width={16}>
                {isAnyBulkAction && (
                  <Checkbox
                    checked={bulk.allDisplayedRecordsAreSelected}
                    disabled={
                      !bulk.isAnySelectable() ||
                      selectAllInvoicesProps.isFetchingInProgress ||
                      selectAllInvoicesProps.isBackendProcessingOngoing
                    }
                    onChange={(e, data) => {
                      bulk.selectAllRecords(false);
                      selectAllInvoicesProps.triggerFetchAllInvoicesIds(false);
                      bulk.toggleAll();
                    }}
                    data-test="select_displayed_invoices"
                    label={<label>Select displayed</label>}
                  />
                )}
              </Grid.Column>
            </Grid.Row>
            {(statusFilter !== IN_PROGRESS_STATUS || !isInternalInvoice) && (
              <Grid.Row columns={1}>
                <Grid.Column width={16}>
                  {selectAllInvoicesProps.isFetchingInProgress && <Loader active inline />}
                  {isAnyBulkAction && !selectAllInvoicesProps.isFetchingInProgress && (
                    <Checkbox
                      checked={bulk.isAllRecordsSelected}
                      disabled={
                        !bulk.isAnySelectable() ||
                        selectAllInvoicesProps.isFetchingInProgress ||
                        selectAllInvoicesProps.isBackendProcessingOngoing ||
                        selectAllInvoicesProps.totalResourceNumber === 0
                      }
                      onChange={(e, data) => {
                        if (bulk.allDisplayedRecordsAreSelected) bulk.toggleAll();
                        selectAllInvoicesProps.triggerFetchAllInvoicesIds(data.checked);
                        bulk.selectAllRecords(data.checked);
                      }}
                      label={
                        bulk.isAllRecordsSelected ? (
                          <label>
                            Select all:{" "}
                            {selectAllInvoicesProps.totalResourceNumber - selectAllInvoicesProps.deselectedRecords.length ||
                              0}{" "}
                            selected
                          </label>
                        ) : (
                          <label>Select all</label>
                        )
                      }
                      data-test="select_all_invoices"
                    />
                  )}
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Grid.Column>
      </Grid.Row>
      <Divider fitted />

      {invoices &&
        invoices.map((invoice, index) =>
          invoice.customer ? (
            <InvoiceRow
              row={index}
              isAllRecordsSelected={bulk.isAllRecordsSelected}
              selectAllInvoicesProps={selectAllInvoicesProps}
              invoice={invoice}
              key={index}
              isDelivered={isDelivered}
              showPaymentColumns={showPaymentColumns}
              handleApprove={handleApprove}
              handleClose={handleClose}
              isOpen={openedInvoice === invoice.id}
              onOpen={onOpen}
              isSelected={
                bulk.isItemSelected(invoice.id) ||
                (bulk.isAllRecordsSelected && !selectAllInvoicesProps.deselectedRecords.includes(invoice.id))
              }
              onCheck={bulk.onCheck}
              isAnyBulkAction={isAnyBulkAction}
              postPaymentsActive={postPaymentsActive}
              isInternalInvoice={isInternalInvoice}
            />
          ) : null
        )}
      {isEmpty(invoices) && (
        <Grid.Row textAlign="center">
          <Grid.Column data-test="not_found">No invoices</Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

export default InvoiceList;
